import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, GitCompareArrows, Loader2, AlertTriangle, FileCheck } from 'lucide-react'
import { useVersionList, useVersionDiff } from '../../hooks/useNegotiations'
import { useTrackChanges } from '../../hooks/useTrackChanges'
import type { DiffBlock, WordChange } from '../../services/negotiationsApi'

interface RedlinePanelProps {
  negotiationId: number
  currentVersion: number
  alwaysExpanded?: boolean
}

export function RedlinePanel({ negotiationId, currentVersion, alwaysExpanded }: RedlinePanelProps) {
  const { t } = useTranslation()
  const { data: versionData } = useVersionList(negotiationId)
  const { applyRedlineAsTrackChanges, isApplying, isSupported: trackChangesSupported } = useTrackChanges()
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded ?? false)
  const [showConfirmApply, setShowConfirmApply] = useState(false)
  const [applyResult, setApplyResult] = useState<{ applied: number; skipped: number } | null>(null)
  const [fromVersion, setFromVersion] = useState(() =>
    currentVersion > 1 ? currentVersion - 1 : 1,
  )
  const [toVersion, setToVersion] = useState(currentVersion)

  const expanded = alwaysExpanded || isExpanded
  const { data: diff, isLoading, error } = useVersionDiff(
    expanded ? negotiationId : undefined,
    expanded ? fromVersion : undefined,
    expanded ? toVersion : undefined,
  )

  const versionOptions = useMemo(() => {
    if (!versionData?.versions) return []
    return versionData.versions
      .map(v => v.version_number)
      .sort((a, b) => a - b)
  }, [versionData])

  if (!versionData || versionData.versions.length < 2) return null

  const handleBlockClick = async (paragraphIndex: number | null) => {
    if (paragraphIndex == null) return
    try {
      await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs
        paragraphs.load('items')
        await context.sync()
        const idx = paragraphIndex - 1 // convert 1-indexed to 0-indexed
        if (idx >= 0 && idx < paragraphs.items.length) {
          paragraphs.items[idx].select('Start')
          await context.sync()
        }
      })
    } catch {
      // Best-effort navigation
    }
  }

  return (
    <div className={alwaysExpanded ? '' : 'border-b border-neutral-100 dark:border-neutral-800'}>
      {!alwaysExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        >
          <GitCompareArrows size={10} />
          {t('negotiate.redline.title', 'Compare versions')}
          <span className="flex-1" />
          {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
      )}

      {expanded && (
        <div className="px-3 pb-2 space-y-2">
          {/* Version selectors */}
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] text-neutral-500 dark:text-neutral-400">
              {t('negotiate.redline.from', 'From')}
            </label>
            <select
              value={fromVersion}
              onChange={e => setFromVersion(Number(e.target.value))}
              className="flex-1 text-[10px] px-1.5 py-0.5 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
            >
              {versionOptions.map(v => (
                <option key={v} value={v}>v{v}</option>
              ))}
            </select>
            <label className="text-[10px] text-neutral-500 dark:text-neutral-400">
              {t('negotiate.redline.to', 'To')}
            </label>
            <select
              value={toVersion}
              onChange={e => setToVersion(Number(e.target.value))}
              className="flex-1 text-[10px] px-1.5 py-0.5 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
            >
              {versionOptions.map(v => (
                <option key={v} value={v}>v{v}</option>
              ))}
            </select>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center gap-1.5 py-4">
              <Loader2 size={12} className="animate-spin text-neutral-400" />
              <span className="text-[10px] text-neutral-400">
                {t('negotiate.redline.loading', 'Comparing versions...')}
              </span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <p className="text-[10px] text-red-500 py-2">
              {t('negotiate.redline.error', 'Could not compare versions.')}
            </p>
          )}

          {/* Diff content */}
          {diff && (
            <>
              {/* Stats bar */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {diff.stats.paragraphs_added > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    +{diff.stats.paragraphs_added}
                  </span>
                )}
                {diff.stats.paragraphs_removed > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    -{diff.stats.paragraphs_removed}
                  </span>
                )}
                {diff.stats.paragraphs_modified > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    ~{diff.stats.paragraphs_modified}
                  </span>
                )}
                {diff.stats.paragraphs_added === 0 &&
                  diff.stats.paragraphs_removed === 0 &&
                  diff.stats.paragraphs_modified === 0 && (
                  <span className="text-[10px] text-neutral-400">
                    {t('negotiate.redline.noChanges', 'No changes between these versions.')}
                  </span>
                )}
              </div>

              {/* Apply as Track Changes */}
              {trackChangesSupported && diff.blocks.some(b => b.type !== 'equal') && (
                <div className="space-y-1">
                  {!showConfirmApply ? (
                    <button
                      onClick={() => setShowConfirmApply(true)}
                      disabled={isApplying}
                      className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isApplying ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        <FileCheck size={11} />
                      )}
                      {t('negotiate.trackChanges.applyButton', 'Apply as Track Changes')}
                    </button>
                  ) : (
                    <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 space-y-1.5">
                      <div className="flex items-start gap-1.5 text-[10px] text-amber-700 dark:text-amber-300">
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                        <span>{t('negotiate.trackChanges.confirmMessage', 'This will enable Track Changes and apply the diff to your document. Changes will appear as your edits. Continue?')}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={async () => {
                            setShowConfirmApply(false)
                            const res = await applyRedlineAsTrackChanges(diff.blocks)
                            setApplyResult({ applied: res.applied, skipped: res.skipped })
                          }}
                          disabled={isApplying}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {isApplying ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            t('negotiate.trackChanges.confirm', 'Apply')
                          )}
                        </button>
                        <button
                          onClick={() => setShowConfirmApply(false)}
                          className="px-2 py-1 text-[10px] font-medium text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
                        >
                          {t('negotiate.trackChanges.cancel', 'Cancel')}
                        </button>
                      </div>
                    </div>
                  )}

                  {applyResult && (
                    <div className="text-[9px] text-neutral-500 dark:text-neutral-400 text-center">
                      {t('negotiate.trackChanges.result', '{{applied}} applied, {{skipped}} skipped', applyResult)}
                    </div>
                  )}
                </div>
              )}

              {/* Diff blocks */}
              <div className="max-h-[300px] overflow-y-auto space-y-px">
                {diff.blocks
                  .filter(b => b.type !== 'equal')
                  .map((block, idx) => (
                    <DiffBlockItem
                      key={idx}
                      block={block}
                      onClick={() =>
                        handleBlockClick(
                          block.paragraph_number_new ?? block.paragraph_number_old,
                        )
                      }
                    />
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function DiffBlockItem({
  block,
  onClick,
}: {
  block: DiffBlock
  onClick: () => void
}) {
  const paragraphNum = block.paragraph_number_new ?? block.paragraph_number_old

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex gap-1.5 p-1 rounded hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
    >
      {/* Paragraph number */}
      <span className="text-[9px] text-neutral-400 w-4 shrink-0 text-right tabular-nums pt-px">
        {paragraphNum != null ? `${paragraphNum}` : ''}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0 text-[10px] leading-relaxed">
        {block.type === 'insert' && (
          <span className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-sm px-0.5">
            {block.new_text}
          </span>
        )}
        {block.type === 'delete' && (
          <span className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 line-through rounded-sm px-0.5">
            {block.old_text}
          </span>
        )}
        {block.type === 'replace' && (
          <WordChanges changes={block.word_changes} />
        )}
      </div>
    </button>
  )
}

function WordChanges({ changes }: { changes: WordChange[] }) {
  return (
    <span>
      {changes.map((change, i) => {
        if (change.type === 'equal') {
          return (
            <span key={i} className="text-neutral-600 dark:text-neutral-400">
              {change.text}{' '}
            </span>
          )
        }
        if (change.type === 'delete') {
          return (
            <span
              key={i}
              className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 line-through rounded-sm px-0.5"
            >
              {change.text}
            </span>
          )
        }
        if (change.type === 'insert') {
          return (
            <span
              key={i}
              className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-sm px-0.5"
            >
              {change.text}
            </span>
          )
        }
        return null
      })}
    </span>
  )
}
